import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Flex, Panel } from '@maxhub/max-ui';
import { EventsPage } from './pages/EventsPage';
import { MyBookingsPage } from './pages/MyBookingsPage';
import { AdminPage } from './pages/AdminPage';
import { LoginPage } from './pages/LoginPage';
import { useInitData } from './hooks/useInitData';
import { useUser } from './hooks/useUser';

// Компонент-защитник для админ-маршрута
const AdminRoute: React.FC = () => {
  const token = localStorage.getItem('adminToken');
  const { initData, isReady } = useInitData();
  const isDev = process.env.NODE_ENV === 'development';

  // В MAX админка доступна через initData (роль admin/owner/tech)
  // В браузере доступна по токену
  const hasAccess = token || initData || isDev;

  if (!isReady) return <div>Загрузка...</div>;

  if (!hasAccess) {
    return <Navigate to="/admin/login" replace />;
  }

  return <AdminPage />;
};

const AppContent: React.FC = () => {
  const { initData, platform, isReady } = useInitData();
  const { user, loading: userLoading } = useUser();
  const location = useLocation();
  const isDev = process.env.NODE_ENV === 'development';
  const token = localStorage.getItem('adminToken');

  // Если мы на странице логина — показываем только логин, без проверок initData
  if (location.pathname === '/admin/login') {
    return <LoginPage />;
  }

  // Ждём инициализации
  if (!isReady || userLoading) {
    return (
      <Flex style={{ justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Загрузка...</div>
      </Flex>
    );
  }

  // Если нет initData и нет токена, и мы не в режиме разработки — показываем предупреждение
  if (!initData && !token && !isDev) {
    return (
      <Flex style={{
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
        height: '100vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 20 }}>⚠️ Приложение работает только внутри MAX</h2>
          <p style={{ marginTop: 16, color: '#666' }}>
            Пожалуйста, откройте это приложение через бота в мессенджере MAX
          </p>
          <p style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
            platform: {platform || 'не определена'}, isDev: {String(isDev)}
          </p>
        </div>
      </Flex>
    );
  }

  // Проверяем, есть ли доступ к админке (роль из MAX или токен веб-админки)
  const isAdmin = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'tech';
  const hasAdminAccess = isAdmin || !!token || isDev;

  return (
    <Panel style={{ paddingBottom: 80 }}>
      <Routes>
        <Route path="/" element={<EventsPage />} />
        <Route path="/my-bookings" element={<MyBookingsPage />} />
        <Route path="/admin" element={<AdminRoute />} />
        <Route path="/admin/login" element={<LoginPage />} />
      </Routes>

      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        padding: '8px 0',
        zIndex: 100
      }}>
        <Link
          to="/"
          style={{
            flex: 1,
            textAlign: 'center',
            textDecoration: 'none',
            color: location.pathname === '/' ? '#007AFF' : '#666',
            padding: '8px 0',
            fontWeight: location.pathname === '/' ? 'bold' : 'normal',
            fontSize: 14
          }}
        >
          📋 Расписание
        </Link>
        <Link
          to="/my-bookings"
          style={{
            flex: 1,
            textAlign: 'center',
            textDecoration: 'none',
            color: location.pathname === '/my-bookings' ? '#007AFF' : '#666',
            padding: '8px 0',
            fontWeight: location.pathname === '/my-bookings' ? 'bold' : 'normal',
            fontSize: 14
          }}
        >
          👤 Личный кабинет
        </Link>
        {hasAdminAccess && (
          <Link
            to="/admin"
            style={{
              flex: 1,
              textAlign: 'center',
              textDecoration: 'none',
              color: location.pathname === '/admin' ? '#007AFF' : '#666',
              padding: '8px 0',
              fontWeight: location.pathname === '/admin' ? 'bold' : 'normal',
              fontSize: 14
            }}
          >
            ⚙️ Админ
          </Link>
        )}
      </div>
    </Panel>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;